import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SupportContact from '../components/SupportContact';
import PurchaseModal from '../components/PurchaseModal';
import OwnerListingActions from '../components/OwnerListingActions';
import { flag } from '../utils/countryFlags';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plane, Calendar, Star, BadgeCheck, Headphones,
  MapPin, Luggage, ArrowLeft, AlertCircle, Clock, Upload, FileText, Loader2, Camera, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BAGGAGE_LABELS = {
  kilos: 'Kilos (kg)',
  enveloppes: 'Enveloppes',
  sac_23kg: 'Sac de 23kg',
  sacs: 'Sacs',
  telephones: 'Telephones',
  ordinateurs: 'Ordinateurs'
};

function getBaggageUnit(type, qty) {
  if (type === 'kilos') return 'kg';
  if (type === 'sacs') return qty > 1 ? 'sacs' : 'sac';
  if (type === 'enveloppes') return qty > 1 ? 'enveloppes' : 'enveloppe';
  if (type === 'sac_23kg') return qty > 1 ? 'sacs 23kg' : 'sac 23kg';
  if (type === 'telephones') return qty > 1 ? 'telephones' : 'telephone';
  if (type === 'ordinateurs') return qty > 1 ? 'ordinateurs' : 'ordinateur';
  return type;
}

function ListingDetailPage() {
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const user = auth.user;
  const token = auth.token;
  const API = auth.API;

  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);

  // Refresh listing data when buy dialog opens to get latest stock
  const handleBuyDialogOpen = useCallback((isOpen) => {
    if (isOpen) {
      // Fetch fresh listing data before opening
      axios.get(API + '/listings/' + id)
        .then(res => setListing(res.data))
        .catch(() => {});
    }
    setBuyDialogOpen(isOpen);
  }, [API, id]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [orderedQuantities, setOrderedQuantities] = useState({});
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [ticketUploadOpen, setTicketUploadOpen] = useState(false);
  const [uploadingTicket, setUploadingTicket] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [listingOrders, setListingOrders] = useState([]);
  const [orderSuccess, setOrderSuccess] = useState(false);

  const handleTicketUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPG ou PNG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5 Mo)');
      return;
    }

    setUploadingTicket(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/listings/${id}/upload-ticket`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Billet téléchargé avec succès !');
      setTicketUploadOpen(false);
      fetchListing(); // Refresh listing data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingTicket(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
    }
  };

  const fetchListing = useCallback(async function() {
    try {
      const res = await axios.get(API + '/listings/' + id);
      setListing(res.data);
      const sRes = await axios.get(API + '/users/' + res.data.seller_id + '/public');
      setSeller(sRes.data);
    } catch (err) {
      toast.error('Annonce non trouvée');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  }, [API, id, navigate]);

  useEffect(function() {
    fetchListing();
  }, [fetchListing]);

  useEffect(function() {
    if (listing && user && user.id === listing.seller_id && token) {
      axios.get(API + '/orders/seller?listing_id=' + listing.id, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(res => setListingOrders(res.data))
        .catch(() => {});
    }
  }, [listing, user, token, API]);

  // Auto-open edit dialog if ?action=edit
  useEffect(function() {
    if (searchParams.get('action') === 'edit' && listing && user && user.id === listing.seller_id) {
      setEditDialogOpen(true);
    }
  }, [searchParams, listing, user]);

  useEffect(function() {
    if (listing) {
      setEditForm({
        departure_city: listing.departure_city || '',
        departure_country: listing.departure_country || '',
        destination_city: listing.destination_city || '',
        destination_country: listing.destination_country || '',
        travel_date: listing.travel_date || '',
        expiration_date: listing.expiration_date || '',
        airline: listing.airline || '',
        baggage_type: listing.baggage_type || 'kilos',
        quantity: listing.quantity || 1,
        price_per_unit: listing.price_per_unit || 0,
        currency: listing.currency || 'CAD',
        baggage_items: listing.baggage_items ? listing.baggage_items.map(function(i) { return Object.assign({}, i); }) : [],
        description: listing.description || '',
        pickup_cities: listing.pickup_cities ? [...listing.pickup_cities] : [],
        delivery_cities: listing.delivery_cities ? [...listing.delivery_cities] : []
      });
    }
  }, [listing]);

  async function handleBuy(purchaseData) {
    if (!user) {
      sessionStorage.setItem('kacos_redirect_after_login', window.location.pathname);
      toast.info('Connectez-vous pour passer une commande.', { duration: 3000 });
      navigate('/login');
      return;
    }
    
    // Check verification before allowing order (at least one)
    if (!user.email_verified && !user.phone_verified) {
      toast.error('Vous devez vérifier au moins votre email ou votre téléphone avant de passer une commande.', { duration: 5000 });
      navigate('/verify');
      return;
    }
    
    const articleDescription = purchaseData.articleDescription;
    const deliveryInstructions = purchaseData.deliveryInstructions || '';
    const selectedBaggage = purchaseData.selectedBaggage;
    const quantity = purchaseData.quantity;
    const isMultiBaggage = purchaseData.isMultiBaggage;
    const paymentMethod = purchaseData.paymentMethod || 'card';
    const declaredValue = purchaseData.declaredValue || 0;
    const insuranceOptIn = purchaseData.insuranceOptIn || false;

    if (!articleDescription || articleDescription.trim().length < 10) {
      toast.error('Décrivez les articles (min 10 caractères)');
      return;
    }

    setProcessing(true);
    try {
      const req = {
        listing_id: id,
        article_description: articleDescription.trim(),
        delivery_instructions: deliveryInstructions.trim() || null,
        origin_url: window.location.origin,
        currency: listing.currency || 'CAD',
        payment_method: paymentMethod,
        declared_value: declaredValue,
        insurance_opt_in: insuranceOptIn
      };

      if (isMultiBaggage && listing.baggage_items && listing.baggage_items.length > 0) {
        const items = [];
        for (let i = 0; i < listing.baggage_items.length; i++) {
          const item = listing.baggage_items[i];
          const qty = selectedBaggage[item.type] || 0;
          if (qty > 0) {
            items.push({
              type: item.type,
              quantity: qty,
              price_per_unit: item.price_per_unit
            });
          }
        }
        req.selected_items = items;
      } else {
        req.quantity = parseFloat(quantity);
      }

      const response = await axios.post(API + '/payments/direct-checkout', req, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      // Order submitted for admin validation
      setBuyDialogOpen(false);
      setProcessing(false);
      setOrderSuccess(true);
    } catch (e) {
      const errorDetail = e.response && e.response.data && e.response.data.detail;
      let msg = 'Erreur lors du paiement';
      if (typeof errorDetail === 'string') {
        msg = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation errors come as array
        msg = errorDetail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        msg = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(msg);
      setProcessing(false);
    }
  }

  async function handleEditListing() {
    // Filter out items with quantity 0 before sending
    const cleanedForm = Object.assign({}, editForm);
    if (cleanedForm.baggage_items) {
      cleanedForm.baggage_items = cleanedForm.baggage_items.filter(function(item) {
        return item.quantity > 0;
      });
    }
    setEditLoading(true);
    try {
      // Admin editing any listing uses admin route, owner uses seller route
      const isAdminEdit = user?.is_admin && (!isOwner || listing?.status === 'expired');
      const url = isAdminEdit
        ? API + '/admin/listings/' + id
        : API + '/listings/' + id;
      const response = await axios.put(url, cleanedForm, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = response.data;
      if (data.needs_reapproval) {
        toast.warning(data.message, { duration: 6000 });
      } else {
        toast.success(data.message);
      }
      if (data.affected_orders_count > 0) {
        toast.info(data.affected_orders_count + ' acheteur(s) notifiés.');
      }
      setEditDialogOpen(false);
      fetchListing();
    } catch (e) {
      const msg = e.response && e.response.data && e.response.data.detail;
      toast.error(msg || 'Erreur');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleCancelListing() {
    if (cancelReason.trim().length < 10) {
      toast.error('Raison: min 10 caractères');
      return;
    }
    setCancelLoading(true);
    try {
      const response = await axios.post(API + '/listings/' + id + '/cancel', {
        reason: cancelReason.trim()
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = response.data;
      toast.success(data.message);
      if (data.refunded_orders > 0) {
        toast.info(data.refunded_orders + ' acheteur(s) remboursés.');
      }
      setCancelDialogOpen(false);
      navigate('/seller?tab=flights');
    } catch (e) {
      const msg = e.response && e.response.data && e.response.data.detail;
      toast.error(msg || 'Erreur');
    } finally {
      setCancelLoading(false);
    }
  }

  function updateEditForm(field, value) {
    setEditForm(function(prev) {
      const next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  async function fetchOrderedQuantities() {
    if (!token || !id) return;
    try {
      const response = await axios.get(API + '/listings/' + id + '/ordered-quantities', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setOrderedQuantities(response.data);
    } catch (e) {
      setOrderedQuantities({});
    }
  }

  function checkNeedsReapproval() {
    if (!listing) return false;
    const simpleFields = ['departure_city', 'departure_country', 'destination_city', 'destination_country', 'travel_date'];
    for (let i = 0; i < simpleFields.length; i++) {
      const f = simpleFields[i];
      if (editForm[f] !== listing[f]) return true;
    }
    // Check pickup/delivery cities arrays
    const origPickup = (listing.pickup_cities || []).join(',');
    const newPickup = (editForm.pickup_cities || []).join(',');
    if (origPickup !== newPickup) return true;
    const origDelivery = (listing.delivery_cities || []).join(',');
    const newDelivery = (editForm.delivery_cities || []).join(',');
    if (origDelivery !== newDelivery) return true;
    return false;
  }

  const isOwner = user && listing && user.id === listing.seller_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  // For multi-baggage, show the lowest price, otherwise use price_per_unit
  let priceDisplay = '0.00';
  if (listing.baggage_items && listing.baggage_items.length > 0) {
    const prices = listing.baggage_items.map(function(i) { return i.price_per_unit || 0; });
    const minPrice = Math.min.apply(null, prices);
    priceDisplay = minPrice.toFixed(2);
  } else if (listing.price_per_unit) {
    priceDisplay = listing.price_per_unit.toFixed(2);
  }
  const unitDisplay = getBaggageUnit(listing.baggage_type, 1);

  // Ticket Upload Modal
  const TicketUploadModal = (
    <Dialog open={ticketUploadOpen} onOpenChange={setTicketUploadOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Télécharger votre billet d'avion
          </DialogTitle>
          <DialogDescription>
            Uploadez une copie de votre billet d'avion pour accélérer la validation de votre annonce.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {uploadingTicket ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Téléchargement en cours...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {/* Option 1: Select file */}
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleTicketUpload}
                  className="hidden"
                  id="ticket-upload-file"
                  disabled={uploadingTicket}
                />
                <label htmlFor="ticket-upload-file" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                    <p className="font-medium text-sm">Choisir un fichier</p>
                    <p className="text-xs text-muted-foreground">PDF, JPG, PNG</p>
                  </div>
                </label>
              </div>
              
              {/* Option 2: Take photo */}
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleTicketUpload}
                  className="hidden"
                  id="ticket-upload-camera"
                  disabled={uploadingTicket}
                />
                <label htmlFor="ticket-upload-camera" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <Camera className="w-8 h-8 text-muted-foreground" />
                    <p className="font-medium text-sm">Prendre une photo</p>
                    <p className="text-xs text-muted-foreground">Caméra</p>
                  </div>
                </label>
              </div>
            </div>
          )}
          
          <p className="text-xs text-center text-muted-foreground">
            Taille maximale : 5 Mo
          </p>
          
          {listing.ticket && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg">
              <BadgeCheck className="w-5 h-5 text-green-500" />
              <span className="text-sm text-green-600">Un billet a déjà été téléchargé</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {TicketUploadModal}

      {/* Order Success Confirmation Dialog */}
      <Dialog open={orderSuccess} onOpenChange={setOrderSuccess}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-lg">Commande envoyée avec succès</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
              <BadgeCheck className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed text-center">
              L'équipe KACOS va vérifier la disponibilité auprès du transporteur et valider votre commande. Vous recevrez un courriel avec les instructions de paiement.
            </p>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-xs text-amber-600 font-medium text-center">
                Vous disposerez de 24h pour effectuer le paiement après réception du courriel, faute de quoi votre commande sera automatiquement annulée.
              </p>
            </div>
            <div className="space-y-2 pt-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Prochaines étapes</p>
              <div className="space-y-0">
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                  <span className="text-sm text-muted-foreground">Validation par l'équipe KACOS</span>
                  <span className="ml-auto text-[10px] text-amber-500 font-medium">En cours</span>
                </div>
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                  <span className="text-sm text-muted-foreground">Paiement</span>
                </div>
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                  <span className="text-sm text-muted-foreground">Ramassage du colis</span>
                </div>
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                  <span className="text-sm text-muted-foreground">Transport</span>
                </div>
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-5 h-5 rounded-full bg-muted text-muted-foreground text-[11px] font-bold flex items-center justify-center flex-shrink-0">5</span>
                  <span className="text-sm text-muted-foreground">Livraison</span>
                </div>
              </div>
            </div>
            <Button className="w-full" onClick={function() { setOrderSuccess(false); navigate('/buyer'); }} data-testid="go-to-orders-btn">
              Voir mes commandes en cours
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 pb-32 lg:pb-24">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate('/listings'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux offres
        </Button>

        {/* Mobile: Price + Buy button at top */}
        {!isOwner && listing.status === 'active' && listing.available_quantity > 0 && (
          <div className="lg:hidden mb-6">
            <Card className="card-dashboard">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {listing.baggage_items && listing.baggage_items.length > 0 ? 'À partir de' : 'Prix par ' + unitDisplay}
                    </p>
                    <p className="font-heading text-2xl font-bold text-primary">{priceDisplay} CAD</p>
                  </div>
                  <Button
                    className="btn-primary px-6 h-11 text-base font-semibold shrink-0"
                    onClick={function() { if (!user) { navigate('/login'); return; } handleBuyDialogOpen(true); }}
                    data-testid="mobile-buy-btn"
                  >
                    Acheter maintenant
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <ListingMainCard listing={listing} />
            {seller && <SellerCard seller={seller} isAdmin={user?.is_admin} />}
          </div>
          <div className="space-y-6">
            <Card className="card-dashboard sticky top-24">
              <CardContent className="pt-6">
                <div className="text-center mb-6">
                  <p className="text-sm text-muted-foreground">
                    {listing.baggage_items && listing.baggage_items.length > 0 ? 'À partir de' : 'Prix par ' + unitDisplay}
                  </p>
                  <p className="font-heading text-4xl font-bold text-primary">{priceDisplay} CAD</p>
                </div>
                <ListingSidebarActions
                  listing={listing}
                  isOwner={isOwner}
                  isAdmin={user?.is_admin}
                  id={id}
                  editForm={editForm}
                  editDialogOpen={editDialogOpen}
                  setEditDialogOpen={setEditDialogOpen}
                  cancelDialogOpen={cancelDialogOpen}
                  setCancelDialogOpen={setCancelDialogOpen}
                  cancelReason={cancelReason}
                  setCancelReason={setCancelReason}
                  editLoading={editLoading}
                  cancelLoading={cancelLoading}
                  handleEditListing={handleEditListing}
                  handleCancelListing={handleCancelListing}
                  updateEditForm={updateEditForm}
                  checkNeedsReapproval={checkNeedsReapproval}
                  navigate={navigate}
                  buyDialogOpen={buyDialogOpen}
                  handleBuyDialogOpen={handleBuyDialogOpen}
                  handleBuy={handleBuy}
                  processing={processing}
                  API={API}
                  token={token}
                  setTicketUploadOpen={setTicketUploadOpen}
                  orderedQuantities={orderedQuantities}
                  fetchOrderedQuantities={fetchOrderedQuantities}
                  listingId={id}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {/* Listing Orders - visible only to the owner, placed below the main grid */}
      {isOwner && listingOrders.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <ListingOrdersSection orders={listingOrders} currency={listing.currency || 'CAD'} isAdmin={user?.is_admin} />
        </div>
      )}

      <Footer />

      {/* Mobile sticky bottom buy button */}
      {!isOwner && listing.status === 'active' && listing.available_quantity > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur border-t border-border px-4 py-3">
          <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
            <div>
              <p className="font-heading text-lg font-bold text-primary">{priceDisplay} CAD</p>
              <p className="text-xs text-muted-foreground">
                {listing.baggage_items && listing.baggage_items.length > 0 ? 'à partir de' : 'par ' + unitDisplay}
              </p>
            </div>
            <Button
              className="btn-primary px-6 h-11 text-base font-semibold"
              onClick={function() { if (!user) { navigate('/login'); return; } handleBuyDialogOpen(true); }}
              data-testid="mobile-sticky-buy-btn"
            >
              Acheter maintenant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ListingSidebarActions(props) {
  const listing = props.listing;
  const isOwner = props.isOwner;
  const isAdmin = props.isAdmin;
  // Only active listings are purchasable
  const isPurchasable = listing.status === 'active' && listing.available_quantity > 0;
  const isExpired = listing.status === 'expired';

  // Status labels and colors
  const statusConfig = {
    pending_verification: { label: 'En approbation', color: 'text-orange-500', bg: 'bg-orange-500/10', icon: '⏳' },
    active: { label: 'Active', color: 'text-green-500', bg: 'bg-green-500/10', icon: '✓' },
    reserved: { label: 'Réservée', color: 'text-amber-500', bg: 'bg-amber-500/10', icon: '' },
    closed: { label: 'Fermée', color: 'text-gray-500', bg: 'bg-gray-500/10', icon: '🔴' },
    expired: { label: 'Expirée', color: 'text-gray-500', bg: 'bg-gray-500/10', icon: '⌛' },
    canceled: { label: 'Annulée', color: 'text-red-500', bg: 'bg-red-500/10', icon: '✗' },
    cancelled: { label: 'Annulée', color: 'text-red-500', bg: 'bg-red-500/10', icon: '✗' },
    rejected: { label: 'Rejetée', color: 'text-red-500', bg: 'bg-red-500/10', icon: '✗' }
  };

  // For pending_verification status - show status to owner/admin only
  if (listing.status === 'pending_verification') {
    if (isOwner) {
      return (
        <div className="space-y-4">
          <div className="text-center py-4 rounded-lg bg-orange-500/10">
            <span className="text-3xl mb-2 block">⏳</span>
            <p className="font-semibold text-lg text-orange-500">En approbation</p>
            <p className="text-sm text-muted-foreground mt-2">Votre annonce est en cours de vérification par notre équipe.</p>
            {!listing.has_ticket && (
              <Button 
                className="mt-4" 
                variant="outline"
                onClick={() => props.setTicketUploadOpen(true)}
                data-testid="upload-ticket-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Ajouter un billet (optionnel)
              </Button>
            )}
          </div>
          <OwnerListingActions
            listing={listing}
            editForm={props.editForm}
            editDialogOpen={props.editDialogOpen}
            setEditDialogOpen={props.setEditDialogOpen}
            cancelDialogOpen={props.cancelDialogOpen}
            setCancelDialogOpen={props.setCancelDialogOpen}
            cancelReason={props.cancelReason}
            setCancelReason={props.setCancelReason}
            editLoading={props.editLoading}
            cancelLoading={props.cancelLoading}
            handleEditListing={props.handleEditListing}
            handleCancelListing={props.handleCancelListing}
            updateEditForm={props.updateEditForm}
            checkIfNeedsReapproval={props.checkNeedsReapproval}
            navigate={props.navigate}
            orderedQuantities={props.orderedQuantities}
            fetchOrderedQuantities={props.fetchOrderedQuantities}
            API={props.API}
            token={props.token}
            listingId={props.listing?.id}
          />
        </div>
      );
    }
    // For admin viewing pending_verification
    if (isAdmin && !isOwner) {
      return (
        <div className="space-y-4">
          <div className="text-center py-4 rounded-lg bg-orange-500/10">
            <span className="text-3xl mb-2 block">⏳</span>
            <p className="font-semibold text-lg text-orange-500">En approbation</p>
            <p className="text-sm text-muted-foreground mt-2">Cette annonce est en attente de validation.</p>
            {!listing.has_ticket && (
              <p className="text-xs text-muted-foreground mt-1">Le vendeur n'a pas encore téléchargé de billet.</p>
            )}
          </div>
          <OwnerListingActions
            listing={listing}
            editForm={props.editForm}
            editDialogOpen={props.editDialogOpen}
            setEditDialogOpen={props.setEditDialogOpen}
            cancelDialogOpen={props.cancelDialogOpen}
            setCancelDialogOpen={props.setCancelDialogOpen}
            cancelReason={props.cancelReason}
            setCancelReason={props.setCancelReason}
            editLoading={props.editLoading}
            cancelLoading={props.cancelLoading}
            handleEditListing={props.handleEditListing}
            handleCancelListing={props.handleCancelListing}
            updateEditForm={props.updateEditForm}
            checkIfNeedsReapproval={props.checkNeedsReapproval}
            navigate={props.navigate}
            orderedQuantities={props.orderedQuantities}
            fetchOrderedQuantities={props.fetchOrderedQuantities}
            API={props.API}
            token={props.token}
            listingId={props.listing?.id}
          />
        </div>
      );
    }
    // Regular users shouldn't see pending_verification listings
    return (
      <div className="text-center py-4 rounded-lg bg-muted">
        <p className="text-muted-foreground">Cette annonce n'est pas encore disponible.</p>
      </div>
    );
    return (
      <div>
        <PurchaseModal
          listing={listing}
          open={props.buyDialogOpen}
          onOpenChange={props.handleBuyDialogOpen}
          onPurchase={props.handleBuy}
          processing={props.processing}
          API={props.API}
          token={props.token}
        />
        <SupportContact
          listingId={props.id}
          triggerButton={
            <Button variant="outline" className="w-full" size="lg" data-testid="contact-support-btn">
              <Headphones className="w-5 h-5 mr-2" /> Contacter le support
            </Button>
          }
        />
      </div>
    );
  }

  if (isPurchasable) {
    if (isOwner) {
      return (
        <OwnerListingActions
          listing={listing}
          editForm={props.editForm}
          editDialogOpen={props.editDialogOpen}
          setEditDialogOpen={props.setEditDialogOpen}
          cancelDialogOpen={props.cancelDialogOpen}
          setCancelDialogOpen={props.setCancelDialogOpen}
          cancelReason={props.cancelReason}
          setCancelReason={props.setCancelReason}
          editLoading={props.editLoading}
          cancelLoading={props.cancelLoading}
          handleEditListing={props.handleEditListing}
          handleCancelListing={props.handleCancelListing}
          updateEditForm={props.updateEditForm}
          checkIfNeedsReapproval={props.checkNeedsReapproval}
          navigate={props.navigate}
          orderedQuantities={props.orderedQuantities}
          fetchOrderedQuantities={props.fetchOrderedQuantities}
          API={props.API}
          token={props.token}
          listingId={props.listing?.id}
        />
      );
    }
    return (
      <div className="space-y-3">
        <PurchaseModal
          listing={listing}
          open={props.buyDialogOpen}
          onOpenChange={props.handleBuyDialogOpen}
          onPurchase={props.handleBuy}
          processing={props.processing}
          API={props.API}
          token={props.token}
        />
        {isAdmin && (
          <OwnerListingActions
            listing={listing}
            editForm={props.editForm}
            editDialogOpen={props.editDialogOpen}
            setEditDialogOpen={props.setEditDialogOpen}
            cancelDialogOpen={props.cancelDialogOpen}
            setCancelDialogOpen={props.setCancelDialogOpen}
            cancelReason={props.cancelReason}
            setCancelReason={props.setCancelReason}
            editLoading={props.editLoading}
            cancelLoading={props.cancelLoading}
            handleEditListing={props.handleEditListing}
            handleCancelListing={props.handleCancelListing}
            updateEditForm={props.updateEditForm}
            checkIfNeedsReapproval={props.checkNeedsReapproval}
            navigate={props.navigate}
            orderedQuantities={props.orderedQuantities}
            fetchOrderedQuantities={props.fetchOrderedQuantities}
            API={props.API}
            token={props.token}
            listingId={props.listing?.id}
          />
        )}
        <SupportContact
          listingId={props.id}
          triggerButton={
            <Button variant="outline" className="w-full" size="lg" data-testid="contact-support-btn">
              <Headphones className="w-5 h-5 mr-2" /> Contacter le support
            </Button>
          }
        />
      </div>
    );
  }

  if (isExpired) {
    if (isAdmin) {
      return (
        <div className="space-y-4">
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-500 font-medium">Annonce expirée</p>
            <p className="text-sm text-muted-foreground">La date de voyage est dépassée</p>
          </div>
          {/* Admin: Edit + Reactivate */}
          <OwnerListingActions
            listing={listing}
            editForm={props.editForm}
            editDialogOpen={props.editDialogOpen}
            setEditDialogOpen={props.setEditDialogOpen}
            cancelDialogOpen={props.cancelDialogOpen}
            setCancelDialogOpen={props.setCancelDialogOpen}
            cancelReason={props.cancelReason}
            setCancelReason={props.setCancelReason}
            editLoading={props.editLoading}
            cancelLoading={props.cancelLoading}
            handleEditListing={props.handleEditListing}
            handleCancelListing={props.handleCancelListing}
            updateEditForm={props.updateEditForm}
            checkIfNeedsReapproval={props.checkNeedsReapproval}
            navigate={props.navigate}
            orderedQuantities={props.orderedQuantities}
            fetchOrderedQuantities={props.fetchOrderedQuantities}
            API={props.API}
            token={props.token}
            listingId={props.listing?.id}
          />
          <ReactivateButton
            type="listing"
            id={props.id}
            listing={listing}
            editForm={props.editForm}
            API={props.API}
            token={props.token}
            onSuccess={props.navigate ? () => window.location.reload() : undefined}
          />
        </div>
      );
    }
    if (isOwner) {
      return (
        <div className="space-y-4">
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-500 font-medium">Annonce expirée</p>
            <p className="text-sm text-muted-foreground">La date de voyage est dépassée</p>
          </div>
          <OwnerListingActions
            listing={listing}
            editForm={props.editForm}
            editDialogOpen={props.editDialogOpen}
            setEditDialogOpen={props.setEditDialogOpen}
            cancelDialogOpen={props.cancelDialogOpen}
            setCancelDialogOpen={props.setCancelDialogOpen}
            cancelReason={props.cancelReason}
            setCancelReason={props.setCancelReason}
            editLoading={props.editLoading}
            cancelLoading={props.cancelLoading}
            handleEditListing={props.handleEditListing}
            handleCancelListing={props.handleCancelListing}
            updateEditForm={props.updateEditForm}
            checkIfNeedsReapproval={props.checkNeedsReapproval}
            navigate={props.navigate}
            orderedQuantities={props.orderedQuantities}
            fetchOrderedQuantities={props.fetchOrderedQuantities}
            API={props.API}
            token={props.token}
            listingId={props.listing?.id}
          />
        </div>
      );
    }
    return (
      <div className="text-center py-4">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="text-red-500 font-medium">Annonce expirée</p>
        <p className="text-sm text-muted-foreground">La date de voyage est dépassée</p>
      </div>
    );
  }

  // Show actual status for non-active listings
  const config = statusConfig[listing.status] || { label: listing.status, color: 'text-gray-500', bg: 'bg-gray-500/10', icon: '•' };
  
  return (
    <div className={`text-center py-6 rounded-lg ${config.bg}`}>
      <span className="text-3xl mb-2 block">{config.icon}</span>
      <p className={`font-semibold text-lg ${config.color}`}>Statut : {config.label}</p>
      {listing.status === 'pending_verification' && (
        <p className="text-sm text-muted-foreground mt-2">Votre annonce est en cours de vérification par notre équipe.</p>
      )}
      {(listing.status === 'canceled' || listing.status === 'cancelled') && (
        <p className="text-sm text-muted-foreground mt-2">Cette annonce a été annulée.</p>
      )}
      {listing.status === 'rejected' && (
        <p className="text-sm text-muted-foreground mt-2">Cette annonce n'a pas été approuvée. Vérifiez vos emails pour plus de détails.</p>
      )}
    </div>
  );
}

function ReactivateButton({ type, id, listing, editForm, API, token, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [travelDate, setTravelDate] = useState(editForm?.travel_date || listing?.travel_date || '');
  const [expirationDate, setExpirationDate] = useState(editForm?.expiration_date || listing?.expiration_date || '');

  const handleReactivate = async () => {
    setLoading(true);
    try {
      const endpoint = type === 'listing'
        ? `${API}/admin/listings/${id}/reactivate`
        : `${API}/admin/orders/${id}/reactivate`;
      const body = type === 'listing'
        ? { travel_date: travelDate, expiration_date: expirationDate }
        : {};
      await axios.post(endpoint, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(type === 'listing' ? 'Annonce réactivée !' : 'Commande réactivée !');
      setShowDialog(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réactivation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        className="w-full bg-green-600 hover:bg-green-700 text-white"
        onClick={() => setShowDialog(true)}
        data-testid="reactivate-btn"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Réactiver {type === 'listing' ? "l'annonce" : 'la commande'}
      </Button>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Réactiver {type === 'listing' ? "l'annonce" : 'la commande'}</DialogTitle>
            <DialogDescription>
              {type === 'listing'
                ? "Modifiez les dates pour réactiver cette annonce. La date de voyage doit être dans le futur."
                : "Cette commande sera remise en attente de paiement."
              }
            </DialogDescription>
          </DialogHeader>
          {type === 'listing' && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Nouvelle date de voyage</Label>
                <Input
                  type="date"
                  value={travelDate}
                  onChange={(e) => setTravelDate(e.target.value)}
                  className="h-9"
                  data-testid="reactivate-travel-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Nouvelle date limite de dépôt</Label>
                <Input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className="h-9"
                  data-testid="reactivate-expiration-date"
                />
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={handleReactivate}
              disabled={loading}
              data-testid="confirm-reactivate-btn"
            >
              {loading ? 'Réactivation...' : 'Confirmer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ListingMainCard(props) {
  const listing = props.listing;
  const travelDate = new Date(listing.travel_date + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  return (
    <Card className="card-dashboard">
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {listing.seller_verified && (
            <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
              <BadgeCheck className="w-3 h-3 mr-1" /> Vendeur vérifié
            </Badge>
          )}
          <Badge variant="outline">{listing.seller_grade}</Badge>
        </div>

        <h1 className="font-heading text-3xl font-bold mb-4">
          {flag(listing.departure_country)} {listing.departure_city}, {listing.departure_country}
          <span className="text-muted-foreground mx-4">→</span>
          {flag(listing.destination_country)} {listing.destination_city}, {listing.destination_country}
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-6 border-y border-border">
          <div>
            <p className="text-sm text-muted-foreground">Date de voyage</p>
            <p className="font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              {travelDate}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Compagnie</p>
            <p className="font-medium flex items-center gap-2">
              <Plane className="w-4 h-4 text-primary" />
              {listing.airline}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Devise</p>
            <p className="font-medium">{listing.currency || 'CAD'}</p>
          </div>
        </div>

        {/* Pickup & Delivery cities */}
        {(listing.pickup_cities?.length > 0 || listing.delivery_cities?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6 border-b border-border">
            {listing.pickup_cities?.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-green-500" />
                  Ville(s) de ramassage
                </p>
                <div className="flex flex-wrap gap-2">
                  {listing.pickup_cities.map((city, i) => (
                    <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/10 text-green-600 border border-green-500/20">
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {listing.delivery_cities?.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-primary" />
                  Ville(s) de livraison
                </p>
                <div className="flex flex-wrap gap-2">
                  {listing.delivery_cities.map((city, i) => (
                    <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <BaggageSection listing={listing} />

        {listing.expiration_date && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm font-medium text-amber-500 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Date limite de dépôt : {new Date(listing.expiration_date).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        )}

        {listing.description && (
          <div className="mt-6">
            <h3 className="font-medium mb-2">Description</h3>
            <p className="text-muted-foreground">{listing.description}</p>
          </div>
        )}

        {/* Price History */}
        <PriceHistorySection listing={listing} />
      </CardContent>
    </Card>
  );
}

function BaggageSection(props) {
  const listing = props.listing;
  const items = listing.baggage_items;
  const currency = listing.currency || 'CAD';

  if (items && items.length > 0) {
    return (
      <div className="py-6 border-b border-border">
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Luggage className="w-5 h-5 text-primary" /> Types de bagages disponibles
        </h3>
        <div className="grid gap-3">
          {items.map(function(item, i) {
            const label = BAGGAGE_LABELS[item.type] || item.type;
            const price = item.price_per_unit ? item.price_per_unit.toFixed(2) : '0.00';
            return (
              <div key={i} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium">{label}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.available_quantity} / {item.quantity} disponible(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-primary">{price} {currency}</p>
                  <p className="text-xs text-muted-foreground">par unité</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const label = BAGGAGE_LABELS[listing.baggage_type] || listing.baggage_type;
  const price = listing.price_per_unit ? listing.price_per_unit.toFixed(2) : '0.00';
  const unit = getBaggageUnit(listing.baggage_type, listing.quantity);

  return (
    <div className="py-6 border-b border-border">
      <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
        <Luggage className="w-5 h-5 text-primary" /> Types de bagages disponibles
      </h3>
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground">
            {listing.available_quantity} / {listing.quantity} {unit}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-primary">{price} {currency}</p>
          <p className="text-xs text-muted-foreground">par unité</p>
        </div>
      </div>
    </div>
  );
}

function SellerCard(props) {
  const seller = props.seller;
  const isAdmin = props.isAdmin;
  const sellerName = isAdmin ? (((seller.first_name || '') + ' ' + (seller.last_name || '')).trim() || 'Vendeur KACOS') : (seller.matricule || ('KK-' + (seller.id || '').substring(0, 8).toUpperCase()));
  const initials = isAdmin
    ? (((seller.first_name || '') + ' ' + (seller.last_name || '')).trim() || 'VK').split(' ').map(function(w) { return w[0]; }).join('').substring(0, 2).toUpperCase()
    : (seller.matricule || 'KK').substring(0, 2).toUpperCase();
  const rating = seller.seller_rating ? seller.seller_rating.toFixed(1) : '0.0';

  return (
    <Card className="card-dashboard" data-testid="seller-card">
      <CardHeader>
        <CardTitle className="text-lg">Vendeur</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <span className="text-2xl font-bold text-white">{initials}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {isAdmin ? (
                <h3 className="font-medium">{sellerName}</h3>
              ) : (
                <h3 className="font-medium font-mono text-muted-foreground">{sellerName}</h3>
              )}
              {seller.profile_verified && <BadgeCheck className="w-5 h-5 text-green-500" />}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {seller.country}
              </span>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" /> {rating}
              </span>
              <span>{seller.total_sales} ventes</span>
            </div>
          </div>
          {isAdmin && (
            <Link to={'/profile/' + seller.id}>
              <Button variant="outline">Voir le profil</Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PriceHistorySection(props) {
  const listing = props.listing;
  const priceHistory = listing.price_history;
  
  if (!priceHistory || priceHistory.length === 0) {
    return null;
  }

  // Sort by date descending (most recent first)
  const sortedHistory = priceHistory.slice().sort(function(a, b) {
    return new Date(b.date) - new Date(a.date);
  });

  // Format relative time
  function getRelativeTime(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 60) {
      return 'il y a ' + diffMinutes + ' min';
    } else if (diffHours < 24) {
      return 'il y a ' + diffHours + 'h';
    } else if (diffDays === 1) {
      return 'hier';
    } else if (diffDays < 7) {
      return 'il y a ' + diffDays + ' jours';
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  }

  return (
    <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border/50">
      <h3 className="font-medium mb-3 flex items-center gap-2 text-sm">
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        Historique des prix
      </h3>
      <div className="space-y-2">
        {sortedHistory.slice(0, 5).map(function(entry, i) {
          const typeLabel = BAGGAGE_LABELS[entry.type] || entry.type || 'Prix';
          const oldPrice = entry.old_price ? entry.old_price.toFixed(2) : '?';
          const newPrice = entry.new_price ? entry.new_price.toFixed(2) : '?';
          const currency = entry.currency || 'CAD';
          const isDecrease = entry.old_price > entry.new_price;
          
          return (
            <div key={i} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className={isDecrease ? 'text-green-500' : 'text-amber-500'}>
                  {isDecrease ? '↓' : '↑'}
                </span>
                <span className="text-muted-foreground">{typeLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="line-through text-muted-foreground">{oldPrice}</span>
                <span className="text-foreground">→</span>
                <span className={isDecrease ? 'text-green-500 font-medium' : 'text-amber-500 font-medium'}>
                  {newPrice} {currency}
                </span>
                <span className="text-xs text-muted-foreground">({getRelativeTime(entry.date)})</span>
              </div>
            </div>
          );
        })}
      </div>
      {sortedHistory.length > 5 && (
        <p className="text-xs text-muted-foreground mt-2">
          +{sortedHistory.length - 5} autres modifications
        </p>
      )}
    </div>
  );
}

function ListingOrdersSection({ orders, currency, isAdmin }) {
  const [expandedId, setExpandedId] = useState(null);

  const STATUS_LABELS = {
    pending_admin_validation: 'En attente',
    pending_payment: 'Paiement en attente',
    paid: 'Payé',
    shipped: 'Expédié',
    in_transit: 'En transit',
    delivered: 'Livré',
    completed: 'Complété',
    cancelled: 'Annulé',
    refunded: 'Remboursé',
    rejected: 'Rejeté'
  };
  const STATUS_COLORS = {
    pending_admin_validation: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
    pending_payment: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    paid: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    shipped: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
    in_transit: 'bg-violet-500/10 text-violet-600 border-violet-500/30',
    delivered: 'bg-green-500/10 text-green-600 border-green-500/30',
    completed: 'bg-green-500/10 text-green-600 border-green-500/30',
    cancelled: 'bg-red-500/10 text-red-600 border-red-500/30',
    refunded: 'bg-gray-500/10 text-gray-500 border-gray-500/30',
    rejected: 'bg-red-500/10 text-red-600 border-red-500/30'
  };
  const TYPE_SHORT = {
    kilos: 'kg', sacs: 'sac', enveloppes: 'env.', telephones: 'tél.',
    ordinateurs: 'ordi.', sac_23kg: 'sac 23kg'
  };

  function formatBaggageInfo(order) {
    if (order.selected_items && order.selected_items.length > 0) {
      return order.selected_items.map(function(it) {
        return it.quantity + ' ' + (TYPE_SHORT[it.type] || it.type);
      }).join(', ');
    }
    if (order.baggage_type) {
      return order.quantity + ' ' + (TYPE_SHORT[order.baggage_type] || order.baggage_type);
    }
    return '-';
  }

  function toggleOrder(id) {
    setExpandedId(function(prev) { return prev === id ? null : id; });
  }

  return (
    <Card className="card-dashboard" data-testid="listing-orders-section">
      <CardContent className="pt-5 pb-3">
        <h3 className="text-sm font-light tracking-wide text-muted-foreground mb-3 uppercase">
          Commandes reçues ({orders.length})
        </h3>
        <div className="divide-y divide-border/40">
          {orders.map(function(order) {
            const buyerName = isAdmin
              ? ((order.buyer_info && order.buyer_info.full_name)
                ? order.buyer_info.full_name
                : (order.buyer_name || ('KK-' + (order.buyer_id || '').substring(0, 8).toUpperCase())))
              : (order.buyer_matricule || (order.buyer_info && order.buyer_info.matricule) || ('KK-' + (order.buyer_id || '').substring(0, 8).toUpperCase()));
            const isExpanded = expandedId === order.id;
            return (
              <div key={order.id} data-testid={'order-row-' + order.id}>
                <button
                  type="button"
                  onClick={function() { toggleOrder(order.id); }}
                  className="w-full flex items-center justify-between py-2.5 gap-2 hover:bg-muted/40 rounded px-2 -mx-2 transition-colors cursor-pointer text-left"
                  data-testid={'order-toggle-' + order.id}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className={'text-[11px] font-light px-2 py-0.5 rounded-full border whitespace-nowrap ' + (STATUS_COLORS[order.current_status] || 'bg-muted text-muted-foreground')}>
                      {STATUS_LABELS[order.current_status] || order.current_status}
                    </span>
                    <span className="text-[11px] font-light text-muted-foreground whitespace-nowrap">
                      {new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </span>
                    <span className="text-[11px] font-light text-muted-foreground truncate hidden sm:inline">
                      {buyerName}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[11px] font-light text-muted-foreground whitespace-nowrap">
                      {formatBaggageInfo(order)}
                    </span>
                    <span className="text-xs font-medium whitespace-nowrap">{order.total?.toFixed(2)} {currency}</span>
                    <svg className={'w-3.5 h-3.5 text-muted-foreground transition-transform ' + (isExpanded ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-2 pb-3 pt-1 -mx-2" data-testid={'order-details-' + order.id}>
                    <div className="bg-muted/30 rounded-lg p-4 space-y-3 text-[12px] font-light text-muted-foreground">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Acheteur</p>
                          <p className="text-foreground font-medium">{buyerName}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Date</p>
                          <p className="text-foreground">{new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Statut</p>
                          <span className={'text-[11px] px-2 py-0.5 rounded-full border ' + (STATUS_COLORS[order.current_status] || '')}>
                            {STATUS_LABELS[order.current_status] || order.current_status}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Bagages</p>
                          <p className="text-foreground">{formatBaggageInfo(order)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Total</p>
                          <p className="text-foreground font-medium">{order.total?.toFixed(2)} {currency}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Votre gain</p>
                          <p className="text-foreground font-medium">{order.seller_amount?.toFixed(2)} {currency}</p>
                        </div>
                      </div>
                      {order.article_description && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Description articles</p>
                          <p className="text-foreground">{order.article_description}</p>
                        </div>
                      )}
                      {order.delivery_instructions && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Instructions livraison</p>
                          <p className="text-foreground">{order.delivery_instructions}</p>
                        </div>
                      )}
                      {order.payment_method && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Paiement</p>
                            <p className="text-foreground">{order.payment_method === 'card' ? 'Carte' : order.payment_method === 'interac' ? 'Interac' : order.payment_method}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider mb-0.5 opacity-60">Séquestre</p>
                            <p className="text-foreground">{order.escrow_status === 'held' ? 'En séquestre' : order.escrow_status === 'released' ? 'Libéré' : order.escrow_status || '-'}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default ListingDetailPage;
